import { configureStore, combineSlices, EnhancedStore } from '@reduxjs/toolkit';
import { isDevelopment } from './constants';
import { baseApiAuthenticated } from '@/api/v2';
import { notificationSlice } from '@/slices/notificationSlice';
import { authMiddleware } from '@/middleware/authMiddleware';
import { registerStore, type LazyLoadedSlicesRegistry } from '@starjack/api';

// Features automatically augment LazyLoadedSlicesRegistry when imported
// No need to import or reference individual slices here!

// eslint-disable-next-line @typescript-eslint/no-empty-object-type, @typescript-eslint/no-empty-interface
export interface LazyLoadedSlices {}

export const rootReducer = combineSlices(
    notificationSlice,
    baseApiAuthenticated
).withLazyLoadedSlices<LazyLoadedSlices>();

// Auto module augmentation - features automatically add themselves to the registry
declare module '@/store' {
    // eslint-disable-next-line @typescript-eslint/no-empty-object-type, @typescript-eslint/no-empty-interface
    export interface LazyLoadedSlices extends LazyLoadedSlicesRegistry {}
}

// Features auto-inject when imported anywhere in the app (via registerSliceInjection)
// No explicit initialization needed - they inject themselves!

// Wrap with clearAuthData logic
const finalReducer = (state, action) => {
    if (action.type === 'authSlice/clearAuthData') {
        return {
            ...rootReducer(undefined, action),
        };
    }

    return rootReducer(state, action);
};

export type Store = Parameters<typeof rootReducer>[0];

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const store: EnhancedStore<Store, any, any> = configureStore({
    reducer: finalReducer,
    middleware: (getDefaultMiddleware) =>
        getDefaultMiddleware({
            serializableCheck: false,
            immutableCheck: false,
        }).concat(authMiddleware, baseApiAuthenticated.middleware),

    devTools: isDevelopment,
});

// Register store in global registry for auto-injection
registerStore(rootReducer, baseApiAuthenticated, store);
